import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';

interface MenuChild {
  path: string;
  label: string;
}

interface MenuItem {
  path?: string;
  label: string;
  icon: () => JSX.Element;
  children?: MenuChild[];
  /** Quando o item tem children: paths que não contam como "ativo" para este grupo (ex.: /campanhas/bot para não marcar Campanhas) */
  excludePrefixes?: string[];
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

export function Navigation() {
  const location = useLocation();
  const { settings, loading } = useSettings();
  const { user } = useAuth();
  const { isOpen, toggle, isMobile, setIsOpen, hoverExpanded, setHoverExpanded, sidebarWidth } = useSidebar();
  const [hasTenantSelected, setHasTenantSelected] = useState(true);

  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const showAdmin = ['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '');

  useEffect(() => {
    if (isSuperAdmin) {
      const checkTenant = () => {
        const selectedTenant = localStorage.getItem('selected_tenant_id') || localStorage.getItem('superadmin_selected_tenant');
        setHasTenantSelected(!!selectedTenant);
      };
      checkTenant();
      window.addEventListener('storage', checkTenant);
      window.addEventListener('focus', checkTenant);
      return () => {
        window.removeEventListener('storage', checkTenant);
        window.removeEventListener('focus', checkTenant);
      };
    } else {
      setHasTenantSelected(true);
    }
  }, [isSuperAdmin]);

  const superAdminSection: MenuSection = {
    label: 'SUPER ADMIN',
    items: [
      { path: '/superadmin/dashboard', label: 'Dashboard', icon: DashboardIcon },
      {
        label: 'Configurações',
        icon: ShieldIcon,
        children: [
          { path: '/superadmin/geral', label: 'Geral' },
          { path: '/superadmin/integracoes', label: 'Integrações' },
          { path: '/superadmin/empresas', label: 'Empresas' },
          { path: '/superadmin/usuarios', label: 'Usuários' },
          { path: '/superadmin/backup', label: 'Backup' },
        ],
      },
    ],
  };

  const gestaoSection: MenuSection = {
    label: 'GESTÃO',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
      {
        label: 'WhatsApp',
        icon: ConnectionIcon,
        children: [
          { path: '/whatsapp', label: 'Conexões' },
          ...(isSuperAdmin ? [{ path: '/aquecimento', label: 'Aquecimento' }] : []),
        ],
      },
      {
        label: 'Audiência',
        icon: ContactsIcon,
        children: [
          { path: '/contatos', label: 'Listar Contatos' },
          { path: '/contatos/importar', label: 'Importar Contatos' },
          { path: '/contatos/extrair', label: 'Extrair Contatos' },
        ],
      },
    ],
  };

  const automacaoSection: MenuSection = {
    label: 'AUTOMAÇÃO',
    items: [
      {
        label: 'Campanhas',
        icon: CampaignsIcon,
        children: [
          { path: '/campanhas', label: 'Nova campanha' },
          { path: '/campanhas/agendamentos', label: 'Agendamentos' },
          { path: '/campanhas/relatorios', label: 'Relatórios' },
        ],
        excludePrefixes: ['/campanhas/bot', '/campanhas/interativa'],
      },
      {
        label: 'Automações',
        icon: AutomationIcon,
        children: [
          { path: '/campanhas/bot', label: 'Chatbot / Fluxos' },
          { path: '/campanhas/interativa', label: 'Campanha Interativa' },
        ],
      },
    ],
  };

  const sistemaSection: MenuSection = {
    label: 'SISTEMA',
    items: [
      { path: '/configuracoes', label: 'Configurações', icon: SettingsIcon },
      { path: '/usuarios', label: 'Usuários', icon: UsersIcon },
    ],
  };

  const sistemaItems = showAdmin
    ? (user?.role === 'SUPERADMIN'
        ? sistemaSection.items
        : sistemaSection.items.filter((i) => i.path === '/configuracoes'))
    : sistemaSection.items.filter((i) => i.path === '/configuracoes');
  const sistemaSectionToUse: MenuSection = { ...sistemaSection, items: sistemaItems };

  const sections: MenuSection[] = hasTenantSelected
    ? isSuperAdmin
      ? [superAdminSection, gestaoSection, automacaoSection, sistemaSection]
      : [gestaoSection, automacaoSection, sistemaSectionToUse]
    : isSuperAdmin
      ? [superAdminSection]
      : [];

  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const showLabels = isOpen || (hoverExpanded && !isMobile);

  // Inicializa todos os dropdowns como abertos
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    sections.forEach((section) => {
      section.items.forEach((item, idx) => {
        if (item.children && item.children.length > 0) {
          const key = `${section.label}-${item.label}-${idx}`;
          initial[key] = true;
        }
      });
    });
    setOpenDropdowns(initial);
  }, [hasTenantSelected]);

  const toggleDropdown = (key: string) => {
    setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isChildActive = (children?: MenuChild[], excludePrefixes?: string[]) => {
    if (!children) return false;
    const pathname = location.pathname;
    if (excludePrefixes?.some((p) => pathname === p || pathname.startsWith(p + '/'))) return false;
    return children.some((c) => pathname === c.path || pathname.startsWith(c.path + '/'));
  };

  const isItemActive = (item: MenuItem) => {
    if (item.path) return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return isChildActive(item.children, item.excludePrefixes);
  };

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[999] lg:hidden"
          aria-hidden
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className="fixed flex flex-col top-0 left-0 h-full transition-all duration-300 ease-in-out z-[1000] border-r border-white/10 overflow-hidden"
        style={{
          width: isMobile && !isOpen ? 0 : sidebarWidth,
          background: 'var(--sidebar-bg)',
          visibility: isMobile && !isOpen ? 'hidden' : 'visible',
          paddingLeft: showLabels ? '1.25rem' : 0,
          paddingRight: showLabels ? '1.25rem' : 0,
        }}
        onMouseEnter={() => !isMobile && !isOpen && setHoverExpanded(true)}
        onMouseLeave={() => setHoverExpanded(false)}
      >
        {/* Logo: recolhido = ícone centralizado; expandido = logo à esquerda */}
        <div className={`shrink-0 flex items-center py-4 px-4 border-b border-white/10 ${showLabels ? 'justify-start' : 'justify-center'}`}>
          {loading ? (
            <div className={`bg-white/20 animate-pulse rounded ${showLabels ? 'h-10 w-28' : 'h-8 w-8'}`} />
          ) : showLabels ? (
            <img src="/assets/logo.png" alt="Logo" className="h-9 object-contain" />
          ) : (
            <img src="/assets/favicon.png" alt="Logo" className="h-8 w-8 object-contain" />
          )}
        </div>

        {/* Nav scroll */}
        <div className="flex flex-col overflow-y-auto flex-1 duration-300 ease-linear no-scrollbar min-h-0 pt-6">
          <nav className="mb-6">
            <div className="flex flex-col gap-6">
              {sections.map((section) => (
                <div key={section.label}>
                  {showLabels && (
                    <h2 className="mb-4 text-xs uppercase tracking-wide text-white/50 font-medium">
                      {section.label}
                    </h2>
                  )}
                  <ul className="flex flex-col gap-1">
                    {section.items.map((item, idx) => {
                      const hasChildren = item.children && item.children.length > 0;
                      const key = `${section.label}-${item.label}-${idx}`;
                      const isDropdownOpen = openDropdowns[key] ?? false;
                      const active = isItemActive(item);

                      return (
                        <li key={key}>
                          {hasChildren ? (
                            <>
                              <button
                                type="button"
                                onClick={() => showLabels && toggleDropdown(key)}
                                className={`group flex items-center gap-3 rounded-xl transition-all duration-200 cursor-pointer ${
                                  showLabels ? 'w-full px-3 py-2.5 justify-start' : 'justify-center w-12 h-12 mx-auto'
                                } ${
                                  active
                                    ? 'bg-white shadow-md'
                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                                }`}
                                style={active ? { color: 'var(--astra-dark-blue)' } : undefined}
                                title={!showLabels ? item.label : undefined}
                              >
                                <span className="shrink-0 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5">
                                  <item.icon />
                                </span>
                                {showLabels && <span className="truncate text-sm font-medium">{item.label}</span>}
                                {showLabels && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="none"
                                    className={`ml-auto w-5 h-5 transition-transform duration-200 shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
                                  >
                                    <path
                                      stroke="currentColor"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="1.5"
                                      d="M4.792 7.396 10 12.604l5.208-5.208"
                                    />
                                  </svg>
                                )}
                                {!showLabels && (
                                  <span className="absolute left-full ml-2 py-1 px-2 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow">
                                    {item.label}
                                  </span>
                                )}
                              </button>
                              {showLabels && (
                                <div
                                  className="overflow-hidden transition-all duration-300"
                                  style={{
                                    height: isDropdownOpen
                                      ? `${(item.children?.length || 0) * 40 + 8}px`
                                      : 0,
                                  }}
                                >
                                  <ul className="mt-2 space-y-1 pb-2">
                                    {(() => {
                                      const children = item.children ?? [];
                                      const pathname = location.pathname;
                                      const isPathExcluded =
                                        item.excludePrefixes?.some((p) => pathname === p || pathname.startsWith(p + '/')) ?? false;
                                      const exactIndex = !isPathExcluded
                                        ? children.findIndex((c) => pathname === c.path)
                                        : -1;

                                      let activeChildIndex = -1;
                                      if (!isPathExcluded && exactIndex >= 0) {
                                        activeChildIndex = exactIndex;
                                      } else if (!isPathExcluded) {
                                        let bestIndex = -1;
                                        let bestLength = -1;
                                        children.forEach((c, idx) => {
                                          if (pathname.startsWith(c.path + '/') && c.path.length > bestLength) {
                                            bestIndex = idx;
                                            bestLength = c.path.length;
                                          }
                                        });
                                        activeChildIndex = bestIndex;
                                      }

                                      return children.map((child, index) => {
                                        const isCurrentPath =
                                          !isPathExcluded &&
                                          (pathname === child.path || pathname.startsWith(child.path + '/'));
                                        const childActive =
                                          isCurrentPath && (activeChildIndex === -1 || index === activeChildIndex);
                                        return (
                                          <li key={`${child.path}-${child.label}`}>
                                            <Link
                                              to={child.path}
                                              className={`flex items-center py-2 pl-11 pr-3 rounded-lg text-sm font-medium transition-colors ${
                                                childActive
                                                  ? 'text-white bg-white/20'
                                                  : 'text-white/70 hover:text-white hover:bg-white/10'
                                              }`}
                                              onClick={() => isMobile && setIsOpen(false)}
                                            >
                                              {child.label}
                                            </Link>
                                          </li>
                                        );
                                      });
                                    })()}
                                  </ul>
                                </div>
                              )}
                            </>
                          ) : (
                            <Link
                              to={item.path!}
                              className={`group flex items-center gap-3 rounded-xl transition-all duration-200 ${
                                showLabels ? 'px-3 py-2.5' : 'justify-center w-12 h-12 mx-auto'
                              } ${
                                active
                                  ? 'bg-white shadow-md'
                                  : 'text-white/80 hover:text-white hover:bg-white/10'
                              }`}
                              style={active ? { color: 'var(--astra-dark-blue)' } : undefined}
                              title={!showLabels ? item.label : undefined}
                              onClick={() => isMobile && setIsOpen(false)}
                            >
                              <span className="shrink-0 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5">
                                <item.icon />
                              </span>
                              {showLabels && <span className="truncate text-sm font-medium">{item.label}</span>}
                              {!showLabels && (
                                <span className="absolute left-full ml-2 py-1 px-2 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow">
                                  {item.label}
                                </span>
                              )}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
}

function ConnectionIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  );
}
function ContactsIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}
function CampaignsIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  );
}
function AutomationIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
function DashboardIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}
